import { ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useLocation } from 'react-router-dom';

import { FormikError, Input } from '@app/form';
import { SubmitButton } from '@app/core';
import { Theme } from '@app/theme';
import { AnalyticsCategoryProvider, Categories } from '@app/analytics';
import { LoginViaCodeModal } from '@app/auth';

const useStyles = createUseStyles((theme: Theme) => ({
  h1: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 2,
  },
  joinContainer: {
    ...theme.fonts.label,
    marginTop: theme.marginBase * 3,
    fontWeight: 400,
  },
  join: {
    minWidth: 0,
    paddingLeft: theme.marginBase / 2,
    '&:hover:not(:disabled)': {
      backgroundColor: 'transparent',
    },
  },
  joinContent: {
    padding: 0,
    '& i': {
      fontWeight: 400,
      fontSize: theme.iconSize.small,
    },
  },
  buttonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const accountSchema = Yup.object().shape({
  email: Yup.string().email().required(),
});

interface Props {
  onSuccess?: () => any;
  children?: ReactNode;
  title?: ReactNode;
  largeButton?: boolean;
}

interface PropsLocation {
  email: string;
}

export const LoginParticipantContent = ({
  onSuccess,
  children,
  title,
  largeButton,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const location = useLocation();
  const state: PropsLocation = location.state as PropsLocation;
  const [email, setEmail] = useState<string | undefined>(state?.email);
  const initialValues = {
    email: state?.email || '',
  };
  const [openModal, setOpenModal] = useState<boolean>(false);

  const onSubmit = async (values: typeof initialValues) => {
    setEmail(values.email);
    setOpenModal(true);
  };

  const onEmailVerified = () => {
    setOpenModal(false);
    if (onSuccess) {
      onSuccess();
    }
  };

  return (
    <AnalyticsCategoryProvider category={Categories.LOGIN}>
      <Formik
        initialValues={initialValues}
        validationSchema={accountSchema}
        onSubmit={onSubmit}
      >
        <Form>
          {!!title && <h1 className={classes.h1}>{title}</h1>}
          {children}
          <Input
            name="email"
            label={t('login.email')}
            placeholder="Ex: albert.einstein@gmail.com"
            type="email"
          />

          <FormikError />

          {largeButton ? (
            <SubmitButton text={t('auth.login.submit')} />
          ) : (
            <div className={classes.buttonContainer}>
              <SubmitButton text={t('auth.login.submit')} small />
            </div>
          )}
        </Form>
      </Formik>
      <LoginViaCodeModal
        email={email}
        isOpen={!!email && openModal}
        onRequestClose={() => {
          setOpenModal(false);
        }}
        onSuccess={onEmailVerified}
      />
    </AnalyticsCategoryProvider>
  );
};
